/**
 * <vector-product-recommendations>
 *
 * Fetches products with the most similar title (cosine similarity on a
 * pre-computed multilingual sentence-embedding) from Typesense and renders
 * them in the same product-item grid used by the native Shopify
 * recommendations section.
 *
 * Strategy:
 *  1. Wait until the element scrolls into view (IntersectionObserver).
 *  2. POST to Typesense /multi_search:
 *       a. Retrieve the current product's title_embedding
 *       b. Run a vector search with that embedding to get top N similar
 *          handles (excluding self, filtered to the current market).
 *  3. Fetch each handle's rendered card via Shopify's Section Rendering
 *     API (/products/{handle}?section_id=vector-product-card) in parallel
 *     so the visual matches the theme's existing product-item exactly.
 *  4. Swap the skeletons for the real cards.
 */

if (typeof VectorProductRecommendations !== 'function') {
  class VectorProductRecommendations extends HTMLElement {
    constructor() {
      super();

      this.config = {
        productId:        this.dataset.productId,
        productHandle:    this.dataset.productHandle,
        market:           this.dataset.market || '',
        sectionId:        this.dataset.sectionId,
        cardSectionId:    this.dataset.cardSectionId || 'vector-product-card',
        host:             this.dataset.typesenseHost,
        apiKey:           this.dataset.typesenseApiKey,
        collection:       this.dataset.typesenseCollection,
        limit:            parseInt(this.dataset.limit, 10) || 4,
        enabledBlocks:    new Set(
          (this.dataset.enabledBlocks || '').split(/\s+/).filter(Boolean)
        ),
      };

      this._grid = this.querySelector('[data-js-vector-reco-grid]');
      this._loaded = false;

      new IntersectionObserver(this._onIntersect.bind(this), {
        rootMargin: '0px 0px 400px 0px',
      }).observe(this);
    }

    _onIntersect(entries, observer) {
      if (!entries[0].isIntersecting || this._loaded) return;
      observer.unobserve(this);
      this._loaded = true;
      this._load().catch((err) => {
        console.error('[vector-recommendations]', err);
        this.setAttribute('data-state', 'empty');
      });
    }

    // -----------------------------------------------------------------
    // Typesense
    // -----------------------------------------------------------------

    async _typesenseMultiSearch(searches) {
      const url = `https://${this.config.host}/multi_search?x-typesense-api-key=${encodeURIComponent(this.config.apiKey)}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ searches }),
      });
      if (!res.ok) throw new Error(`Typesense ${res.status}`);
      return res.json();
    }

    async _fetchSimilarHandles() {
      // Step 1 — get the seed embedding
      const seed = await this._typesenseMultiSearch([{
        collection: this.config.collection,
        q: '*',
        filter_by: `id:=${this.config.productId}`,
        per_page: 1,
        include_fields: 'id,title_embedding',
      }]);

      const seedHit = seed.results?.[0]?.hits?.[0];
      if (!seedHit || !seedHit.document?.title_embedding) {
        return [];
      }
      const vec = seedHit.document.title_embedding;

      // Step 2 — vector search for similar products
      const filters = [`id:!=${this.config.productId}`];
      if (this.config.market) filters.push(`markets:=[${this.config.market}]`);

      const similar = await this._typesenseMultiSearch([{
        collection: this.config.collection,
        q: '*',
        vector_query: `title_embedding:([${vec.join(',')}], k:${this.config.limit + 1})`,
        filter_by: filters.join(' && '),
        per_page: this.config.limit,
        include_fields: 'id,handle,title',
      }]);

      const hits = similar.results?.[0]?.hits || [];
      return hits.map((h) => h.document.handle).filter(Boolean);
    }

    // -----------------------------------------------------------------
    // Shopify card rendering
    // -----------------------------------------------------------------

    async _fetchCardHTML(handle) {
      const url = `/products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(this.config.cardSectionId)}`;
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) return null;
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const card = doc.querySelector('[data-js-vector-product-card]');
      if (!card) return null;
      this._applyBlockVisibility(card);
      return card.innerHTML;
    }

    // Remove DOM pieces whose corresponding block is not enabled on the
    // parent <vector-product-recommendations> section. The card itself
    // is rendered by `vector-product-card.liquid` which has no schema
    // settings of its own (Section Rendering API can't receive custom
    // setting values), so visibility is enforced on the client.
    _applyBlockVisibility(card) {
      const enabled = this.config.enabledBlocks;
      const map = {
        vendor:       '.product-item__vendor',
        title:        '.product-item__title',
        price:        '.product-item__price',
        rating:       '.product-item__rating',
        quick_buy:    '.product-item__quick-buy',
        product_link: '.product-item__product-link',
      };
      for (const [type, selector] of Object.entries(map)) {
        if (!enabled.has(type)) {
          card.querySelectorAll(selector).forEach((el) => el.remove());
        }
      }
    }

    // -----------------------------------------------------------------
    // Main flow
    // -----------------------------------------------------------------

    async _load() {
      const handles = await this._fetchSimilarHandles();
      if (!handles.length) {
        this.setAttribute('data-state', 'empty');
        return;
      }

      const cards = await Promise.all(handles.map((h) => this._fetchCardHTML(h)));
      const validCards = cards.filter(Boolean);

      if (!validCards.length) {
        this.setAttribute('data-state', 'empty');
        return;
      }

      this._grid.innerHTML = validCards
        .map((html) => `<div class="product-item-wrapper">${html}</div>`)
        .join('');

      // Unwrap the helper wrappers so the children sit directly in the grid
      // (the product-item card is its own top-level element).
      this._grid.querySelectorAll('.product-item-wrapper').forEach((w) => {
        while (w.firstChild) w.parentNode.insertBefore(w.firstChild, w);
        w.remove();
      });

      // Inline deferred forms (matches ProductRecommendations behaviour)
      this._grid.querySelectorAll('form').forEach((form) => {
        const tpl = form.querySelector('template');
        if (tpl) form.append(tpl.content.cloneNode(true));
      });

      this.setAttribute('data-state', 'loaded');
      this.dispatchEvent(new Event('vector-recommendations-loaded'));
      // Fire the same event the native ProductRecommendations dispatches, so
      // any existing listeners in the theme see this section as equivalent.
      this.dispatchEvent(new Event('product-recommendations-loaded'));
    }
  }

  if (typeof customElements.get('vector-product-recommendations') === 'undefined') {
    customElements.define('vector-product-recommendations', VectorProductRecommendations);
  }
}
